import clsx from 'clsx'
import {
    ContentActionView,
    ContentView,
    EnrichedContent,
    ProfileViewWithShadowBan,
    TranslatedTextView,
    useCreateReadReceiptMutation,
    useDeleteReadReceiptMutation,
    useGetScreenshotByContentIdQuery
} from '../../../api/client'
import avatar from '../../../assets/avatar.jpeg'
import { generateContentTypeFontAwesomeName } from '../../../util/classNames'
import ActionRow from '../../actions/ActionRow'
import { toastError, toastSuccess } from '../../Notification'
import ContentClassificationList from './ContentClassificationList'
import ContentLink from './ContentLink'
import ContentReplyTo from './ContentReplyTo'
import ContentSelector from './ContentSelector'
import ContentSidebar from './ContentSidebar'
import ContentText from './ContentText'
import ImageAbuseSummary from './ImageAbuseSummary'
import ContentMetadata from './metadata/ContentMetadata'
import ReplyText from './ReplyText'
import { useRecoilState } from 'recoil'
import { canViewScreenshotsState, isUserAdminState, userState } from '../../../store/DomainStore'

// In the future, I think we should do an enum with actions.
const REPLY_ACTION = 'REPLY'

type Props = {
    content: ContentView
    showAccount: Boolean
    onContentAction: (contentActions: ContentActionView[], queued?: boolean) => void
    enriching: boolean
    translatedText?: TranslatedTextView
    translating: boolean
    detectedLanguage: string | null
    onTranslateClick: () => void
    profileCache: ProfileViewWithShadowBan[]
    enrichedContentMap: Map<number, EnrichedContent>
    actionCacheMap: Map<number, ContentActionView[]>
    isSelected: boolean
    onSelectionChange: (content: ContentView, isSelected: boolean) => void
    // this is a hack Zoe made me do it
    rounded?: boolean
    enableBulkActions?: boolean
    score?: number
    onMarkAsReadClick: (content: ContentView) => void
    onMarkAsUnreadClick: (content: ContentView) => void
    showReplyButton?: boolean
    onShadowBanChange?: (contentId: number, profileId: number) => void
    newProfileIds?: Map<number, number>
}

function ContentCard(props: Props) {
    const {
        content,
        showAccount,
        onContentAction,
        enriching,
        translatedText,
        translating,
        detectedLanguage,
        onTranslateClick,
        profileCache,
        enrichedContentMap,
        actionCacheMap,
        rounded,
        isSelected,
        onSelectionChange,
        enableBulkActions,
        score,
        onMarkAsReadClick,
        onMarkAsUnreadClick,
        showReplyButton = true,
        onShadowBanChange,
        newProfileIds
    } = props

    const contentActionClicked = async function (actions: ContentActionView[]) {
        onContentAction(actions)
    }

    // This is used to help manage the read receipts
    const [user] = useRecoilState(userState)
    const [canViewScreenshots] = useRecoilState(canViewScreenshotsState)
    const [isUserAdmin] = useRecoilState(isUserAdminState)

    /** ******************************************************
        READ RECEIPT
        */

    const [createReadReceiptMutation, { loading: createReadReceiptLoading }] =
        useCreateReadReceiptMutation({
            onError: (error) => {
                toastError(error.message)
            },
            onCompleted: (result) => {
                toastSuccess(`Marked as read`)
                content.readReceipts.push(result.createReadReceipt)
            }
        })

    const [deleteReadReceiptMutation, { loading: deleteReadReceiptLoading }] =
        useDeleteReadReceiptMutation({
            onError: (error) => {
                toastError(error.message)
            },
            onCompleted: (result) => {
                content.readReceipts = content.readReceipts.filter(
                    (receipt) => receipt.userId !== user.id
                )
                toastSuccess(`Marked as unread`)
            }
        })

    /** ******************************************************
        SCREENSHOT
        */

    const { data: screenshotData, refetch: refetchScreenshot } = useGetScreenshotByContentIdQuery({
        variables: { contentId: content.id },
        fetchPolicy: 'cache-first',
        skip: !content.id || (!canViewScreenshots && !isUserAdmin)
    })

    // Pass the screenshot regardless of status - ContentMoreActions handles display logic
    // based on status (success, failed, pending) and whether screenshots are enabled for the account
    const screenshot = screenshotData?.getScreenshotByContentId ?? null

    let moderationStyle = content.moderation?.toLowerCase()
    let name = content.account.serviceAccountUsername?.startsWith('u_')
        ? 'reddit'
        : content.socialMediaServiceName.toLowerCase()
    let iconName = generateContentTypeFontAwesomeName(content.contentType, false, name)

    // The key here is the serviceAuthorId - we convert to a map as it is a faster lookup.
    const profileMap = new Map<number, ProfileViewWithShadowBan>(
        profileCache.map((profile) => [profile.id, profile])
    )

    // Extract the profile from the profileMap
    const profilePath =
        profileMap?.get(content.authorProfileId || -1)?.profileImagePath || undefined

    // Extract the profile from the profileMap
    const enrichedContent = enrichedContentMap.get(content.id)

    const cardClasses = `${isSelected ? 'bg-arwen-100' : ''}`

    // Identify the relevant actions from the action cache map
    const allExistingContentActions = actionCacheMap.get(content.id) || []
    const reply = allExistingContentActions.find(
        (action) => action.action === REPLY_ACTION && action.active
    )

    const handleMarkAsReadClick = async function (content: ContentView) {
        await createReadReceiptMutation({
            variables: { contentId: content.id }
        })

        onMarkAsReadClick(content)
    }

    const handleMarkAsUnreadClick = async function (content: ContentView) {
        await deleteReadReceiptMutation({
            variables: { contentId: content.id }
        })

        onMarkAsUnreadClick(content)
    }

    return (
        <div key={content.id} className={`group @container ${cardClasses}`}>
            <div className="flex flex-row relative">
                {enableBulkActions && (
                    <ContentSelector
                        content={content}
                        isSelected={isSelected}
                        onSelectionChange={onSelectionChange}
                    />
                )}
                <ContentSidebar
                    iconName={iconName}
                    moderationStyle={moderationStyle}
                    isPublic={content.isPublic}
                    socialMediaServiceName={content.socialMediaServiceName}
                    rounded={rounded}
                    isSelected={isSelected}
                />
                {content.account.profileCollectionEnabled && (
                    <div className="ml-2 mt-2 flex shrink-0">
                        <img
                            className="h-8 w-8 @sm:h-10 @sm:w-10 @lg:h-14 @lg:w-14 rounded-full border-slate-300 border shadow-md"
                            src={profilePath || avatar}
                            onError={(event) => {
                                //@ts-ignore
                                event.target.onerror = null
                                //@ts-ignore
                                event.target.src = avatar
                            }}
                            alt="avatar"
                        />
                    </div>
                )}
                <div
                    className={clsx(
                        // min-w-0 so the column can shrink below its content and the
                        // metadata row truncates rather than pushing the card wider.
                        'flex min-w-0 flex-grow flex-col justify-between pb-2 pl-4 pr-4 pt-3'
                    )}
                >
                    <div>
                        <div className="mb-1 flex min-w-0 flex-row items-baseline">
                            <ContentMetadata
                                content={content}
                                enrichedContent={enrichedContent}
                                profileCache={profileCache}
                                onShadowBanChange={onShadowBanChange}
                                newProfileIds={newProfileIds}
                                screenshot={screenshot}
                                onScreenshotRefetch={refetchScreenshot}
                                iconName={iconName}
                                moderationStyle={moderationStyle}
                            />

                            {/*
                             * The two wrappers are `contents`, so score, account name and links
                             * are direct items of this row and share one flex shrink pass with
                             * the metadata. The account name has the highest shrink factor, so
                             * it gives way first and freezes at 0; score, links, dates, menu and
                             * Ad never shrink; the author name and handle truncate last. A
                             * shrinkable wrapper instead took ~99% of any overflow and squeezed
                             * the links past their own shrink-0 minimum, so they spilled out of
                             * the row. The metadata's `mr-auto` keeps these items at the end.
                             */}
                            <div className="contents">
                                <div className="contents">
                                    {score ? (
                                        <p className="hidden shrink-0 pr-2 font-bold @sm:block">
                                            {Math.round(score * 100)}%
                                        </p>
                                    ) : null}
                                    {showAccount && (
                                        <p className="hidden min-w-0 max-w-[12rem] shrink-[100] truncate pr-2 text-ink-700 @3xl:block">
                                            <span className="">{content.account?.accountName}</span>
                                        </p>
                                    )}
                                    <ContentLink
                                        content={content}
                                        enrichedContent={enrichedContent}
                                        enriching={enriching}
                                    />
                                </div>
                            </div>
                        </div>
                        {/*
                         * Own line at every width. As a third flex child it took a share of
                         * the row from the metadata (zero flex basis), which then collapsed
                         * under its own shrink-0 date and menu at wide card widths.
                         */}
                        <ContentReplyTo content={content} />
                        <ContentText
                            content={content}
                            detectedLanguage={detectedLanguage}
                            translatedText={translatedText}
                            translating={translating}
                            onTranslateClick={onTranslateClick}
                            enrichedContent={enrichedContent}
                        />
                    </div>
                    <div className="mt-1 text-ink-700">
                        {/*
                         * The classification pills wrap inside their own column
                         * rather than widening it: `min-w-0 flex-1` lets the
                         * column shrink below its content width (a flex item's
                         * default `min-width: auto` is what stops that), and
                         * `shrink-0` holds the actions at full width. Without
                         * the pair, a comment flagged by several classifiers
                         * pushed past the row and `flex-wrap` dropped the whole
                         * action row onto the next line, so the buttons moved
                         * from card to card. — Claude
                         */}
                        <div className="flex flex-row items-end justify-between gap-x-4">
                            <div className="flex min-w-0 flex-1 flex-col">
                                <div className="flex min-w-0 flex-row">
                                    <ContentClassificationList content={content} />
                                </div>
                                <div className="mt-1 flex min-w-0 flex-row">
                                    <ImageAbuseSummary
                                        media={content.media}
                                        contentId={content.id}
                                        contentType={content.contentType}
                                    />
                                </div>
                            </div>
                            <div className="flex shrink-0 flex-row items-center ">
                                {content.account && (
                                    <ActionRow
                                        content={content}
                                        onActionClick={contentActionClicked}
                                        profileCache={profileCache}
                                        actionCacheMap={actionCacheMap}
                                        onMarkAsReadClick={handleMarkAsReadClick}
                                        onMarkAsUnreadClick={handleMarkAsUnreadClick}
                                        readReceiptLoading={
                                            createReadReceiptLoading || deleteReadReceiptLoading
                                        }
                                        showReplyButton={showReplyButton}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {reply?.message && (
                        <ReplyText
                            reply={reply.message}
                            content={content}
                            onActionClick={contentActionClicked}
                            profileCache={profileCache}
                            actionCacheMap={actionCacheMap}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default ContentCard
